import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { ComboboxInputField } from "@/components/shared/form";
import { Info, PencilIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useOutletContext } from "react-router-dom";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { FetchUsersQuery, FetchUsersQueryVariables, FetchUsersDocument, User } from "@/graphql/__generated__/graphql";
import { useUpdateChanceAdminMutationHook } from "@/hooks";
import { Card } from "@/components/ui/card";
import { CircleLoader } from "@/components/shared/loaders";
import { useQuery } from "@apollo/client/react";
import { useTenderPermissionsData } from "@/hooks/cache";

/**
 * Create a form schema for updating the owner of a chance
 * @param t - The translation function
 * @returns A form schema for updating the owner of a chance
 */
const createUpdateOwnerFormSchema = (t: TFunction) => {
  return z.object({
    ownerId: z.string({
      error: (issue) => issue.input === undefined ?
        t("schemas:form.required")
        :
        t("schemas:form.invalid")
    }),
  });
};

/**
 * Props for the UpdateChanceOwnerDialog component
 * @param currentOwner - The current owner of the chance
 */
interface UpdateChanceOwnerDialogProps {
  currentOwner?: User;
}

/**
 * Update the owner of a chance
 * @param currentOwner - The current owner of the chance
 * @returns A dialog to update the owner of a chance
 */
function UpdateChanceOwnerDialog({ currentOwner }: UpdateChanceOwnerDialogProps) {
  const { t } = useTranslation();
  const {
    fetchTenderById: {
      id: tenderId,
      description: { name: chanceName } = {}
    } = {}
  } = useOutletContext<EditTenderContext>().tenderByIdData || {};
  const { canEditDetails } = useTenderPermissionsData(Number(tenderId));

  const { updateChanceAdminMutation, loading } = useUpdateChanceAdminMutationHook();
  const { data: usersData } = useQuery<FetchUsersQuery, FetchUsersQueryVariables>(FetchUsersDocument, {
    fetchPolicy: "cache-and-network"
  });

  const [isOpen, setIsOpen] = useState(false);
  const users = usersData?.fetchUsers ?? [];

  const formSchema = createUpdateOwnerFormSchema(t);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset({ ownerId: "" });
    }
    setIsOpen(open);
  };

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (data.ownerId === currentOwner?.id?.toString()) {
      return;
    }

    updateChanceAdminMutation({
      variables: {
        tenderId: Number(tenderId),
        newChanceAdminId: Number(data.ownerId),
      },
    });
    setIsOpen(false);
  };

  if (!canEditDetails) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost">
          <PencilIcon className="w-2 h-2 mr-1" /> {t("common:buttons.edit")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{t("modals:updatechanceowner.title")}</DialogTitle>
              <DialogDescription>{t("modals:updatechanceowner.description", { chanceName })}</DialogDescription>
            </DialogHeader>
            <Card className="p-2 text-xs flex items-start gap-2 shadow-none">
              <Info className="w-4 h-4 shrink-0" />
              <p>
                {t("modals:updatechanceowner.disclaimer")}
              </p>
            </Card>
            <ComboboxInputField
              form={form}
              name="ownerId"
              label={t("modals:updatechanceowner.inputs.owner.label")}
              className="w-full"
              disabled={loading}
              options={users.map(user => ({
                label: `${user.firstname} ${user.lastname}${user.id.toString() === (currentOwner?.id?.toString() ?? "") ? " - " + t("modals:updatechanceowner.currentowner") : ""}`,
                value: user.id.toString(),
                disabled: user.id.toString() === currentOwner?.id?.toString()
              }))}
              searchPlaceholder={t("common:search")}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" type="button" disabled={loading}>{t("common:buttons.cancel")}</Button>
              </DialogClose>
              <Button variant="default" type="submit" disabled={loading} className="gap-2">
                <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
                {t("common:buttons.update")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default UpdateChanceOwnerDialog;
